import { TFunction } from 'react-i18next';
import moment from 'moment';
import * as yup from 'yup';

import { getCountryCallingCodeAndNationalNumber } from 'utils/phone';
import { PersonsDocumentsType } from 'entities/dashboard';
import { calcProcentege } from 'pages/dashboard/utils';
import { OwnerTypes } from 'entities/ownership';
import {
  stringRequired,
  validateCity,
  validateEmail,
  validatePhone,
  validatePostalCode,
  validateState,
} from 'utils/validation';

import { IdTypes } from './constants';
import { CountryISOCodes } from 'common/countries';

const getDescriptionById = (
  types: PersonsDocumentsType[],
  id: number | string,
): string =>
  types.find((type) => type.personDocumentsTypesId === Number(id))
    ?.description || '';

export const getOwnersPersonSchema = (
  t: TFunction,
  totalShare: number,
  types: PersonsDocumentsType[],
) =>
  yup
    .object()
    .shape({
      ownershipPercentage: yup
        .number()
        .typeError(
          t('ownership:fields.ownershipPercentage.validation.required'),
        )
        .min(
          0.01,
          t('ownership:fields.ownershipPercentage.validation.min', {
            value: 0,
          }),
        )
        .max(
          100 - totalShare,
          t('ownership:fields.ownershipPercentage.validation.total'),
        )
        .required(
          t('ownership:fields.ownershipPercentage.validation.required'),
        ),
      givenName1: stringRequired(t, 'common:fields.firstname.label'),
      surnameFirst: stringRequired(t, 'common:fields.lastname.label'),
      dateOfBirth: yup
        .date()
        .typeError(t('ownership:fields.dateOfBirth.validation.valid'))
        .test(
          'dateOfBirth',
          t('ownership:fields.dateOfBirth.validation.valid'),
          (value) => {
            const years = moment().diff(moment(value), 'years');
            return years > 16 && years < 120;
          },
        )
        .required(t('ownership:fields.dateOfBirth.validation.required')),
      countryIsocode: stringRequired(t, 'common:fields.country.label'),
      addressLine1: stringRequired(t, 'common:fields.address.label'),
      city: validateCity(t),
      postalCode: validatePostalCode(t),
      stateOrProvince: validateState(t),
      emailAddress: validateEmail(t),
      phone: validatePhone(t),
      personDocumentsTypesId: stringRequired(
        t,
        'ownership:fields.idType.label',
      ),
      idTypeName: yup.string().when('personDocumentsTypesId', {
        is: (id: number) => getDescriptionById(types, id) === IdTypes.Other,
        then: stringRequired(t, 'ownership:fields.idTypeName.label'),
        otherwise: yup.string().notRequired(),
      }),
      idnumber: yup
        .string()
        .transform((value, originalValue) =>
          originalValue == null ? '' : value,
        )
        .trim()
        .required(
          t('common:validations.required', {
            name: t('ownership:fields.idnumber.label'),
          }),
        )
        .when('personDocumentsTypesId', {
          is: (id: number) => getDescriptionById(types, id) === IdTypes.SSN,
          then: yup
            .string()
            .required(
              t('common:validations.required', {
                name: t('ownership:fields.idnumber.label'),
              }),
            )
            .length(11, t('ownership:fields.idnumber.validation.ssn')),
          otherwise: yup.string().when('personDocumentsTypesId', {
            is: (id: number) =>
              getDescriptionById(types, id) === IdTypes.Passport,
            then: yup
              .string()
              .required(
                t('common:validations.required', {
                  name: t('ownership:fields.idnumber.label'),
                }),
              )
              .min(6, t('ownership:fields.idnumber.validation.passport'))
              .max(12, t('ownership:fields.idnumber.validation.passport')),
            otherwise: yup.string().required(
              t('common:validations.required', {
                name: t('ownership:fields.idnumber.label'),
              }),
            ),
          }),
        }),
      idcountryIsocode: stringRequired(
        t,
        'ownership:fields.idIssuingCountry.label',
      ),
    })
    .required();

export const getOwnersBusinessSchema = (t: TFunction, totalShare: number) =>
  yup
    .object()
    .shape({
      businessLegalName: stringRequired(
        t,
        'common:fields.businessLegalName.label',
      ),
      ownershipPercentage: yup
        .number()
        .typeError(
          t('ownership:fields.ownershipPercentage.validation.required'),
        )
        .min(
          0.01,
          t('ownership:fields.ownershipPercentage.validation.min', {
            value: 0,
          }),
        )
        .max(
          100 - totalShare,
          t('ownership:fields.ownershipPercentage.validation.total'),
        )
        .required(
          t('ownership:fields.ownershipPercentage.validation.required'),
        ),
      addressLine1: stringRequired(t, 'common:fields.address.label'),
      city: validateCity(t),
      postalCode: validatePostalCode(t),
      stateOrProvince: validateState(t),
      countryIsocode: stringRequired(t, 'common:fields.country.label'),
      emailAddress: validateEmail(t, 'ownership:fields.businessEmail.label'),
      phone: validatePhone(t, 'ownership:fields.businessPhone.label'),
      countryofIncorporationIsocode: stringRequired(
        t,
        'common:fields.country.label',
      ),
    })
    .required();

export const getMappedOwner = (owner) => ({
  ...owner,
  addressLine2: owner.addressLine2 ? owner.addressLine2.trim() : '',
  addressLine3: owner.addressLine3 ? owner.addressLine3.trim() : '',
  emailAddress: owner.emailAddress ? owner.emailAddress.trim() : '',
  stateOrProvince: owner.stateOrProvince ? owner.stateOrProvince.trim() : '',
  type: owner.businessLegalName ? OwnerTypes.business : OwnerTypes.person,
  dateOfBirth: owner.dateOfBirth ? new Date(owner.dateOfBirth) : null,
  phone: `+${owner.countryCallingCode?.replace('+', '') || ''}${
    owner.telephoneNumber || owner.businessContactNumber || ''
  }`,
  alpha2Code: owner.addressAlpha2Code || 'US',
  idnumber: owner.idnumber || '',
  personDocumentsTypesId: owner.personDocumentsTypesId || '',
  idTypeName: owner.idTypeName || '',
  idcountryIsocode: owner.idcountryIsocode || '',
});

export const getMappedOwners = (owners) =>
  owners.map((owner) => getMappedOwner(owner));

export const getMappedOwnerPersonFields = ({ data, business }) => {
  const { countryCallingCode, nationalNumber } =
    getCountryCallingCodeAndNationalNumber(data.phone);

  return {
    ...data,
    isActive: true,
    businessesId: business.businessesId,
    countryCallingCode: countryCallingCode,
    telephoneNumber: nationalNumber,
  };
};

export const getFormFields = (values) => {
  const fields: any = {
    ownershipPercentage: values.ownershipPercentage,
    countryIsocode: values.countryIsocode,
    addressLine1: values.addressLine1,
    city: values.city,
    postalCode: values.postalCode,
    phone: values.phone,
  };

  if (values.countryIsocode !== CountryISOCodes.UnitedKingdom) {
    fields.stateOrProvince = values.stateOrProvince;
  }

  if (!values.type || values.type === OwnerTypes.person) {
    const personFields: any = {
      ...fields,
      givenName1: values.givenName1,
      surnameFirst: values.surnameFirst,
      dateOfBirth: values.dateOfBirth,
      emailAddress: values.emailAddress,
      idfrontGuid: values.idfrontGuid,
      personDocumentsTypesId: values.personDocumentsTypesId,
      idnumber: values.idnumber,
      idcountryIsocode: values.idcountryIsocode,
    };

    return personFields;
  }

  if (values.type === OwnerTypes.business) {
    return {
      ...fields,
      businessLegalName: values.businessLegalName,
      countryofIncorporationIsocode: values.countryofIncorporationIsocode,
    };
  }

  return fields;
};

export const getMappedCreateOwnerPersonFields = ({
  data,
  person,
  business,
}) => {
  const fields = getMappedOwnerPersonFields({ data, business });

  return {
    ...fields,
    createdBy: person.emailAddress,
  };
};

export const getMappedUpdateOwnerPersonFields = ({
  data,
  person,
  business,
}) => {
  const fields = getMappedOwnerPersonFields({ data, business });

  return {
    ...fields,
    addressLine2: data.addressLine2 || ' ',
    modifiedBy: person.emailAddress,
    businessesOwnersPersonsId: data.businessesOwnersPersonsId,
  };
};

export const getMappedOwnerBusinessFields = ({ data, business }) => {
  const { countryCallingCode, nationalNumber } =
    getCountryCallingCodeAndNationalNumber(data.phone);

  return {
    isActive: true,
    businessesId: business.businessesId,
    businessLegalName: data.businessLegalName,
    ownershipPercentage: data.ownershipPercentage,
    addressLine1: data.addressLine1,
    addressLine2: data.addressLine2,
    addressLine3: data.addressLine3,
    city: data.city,
    postalCode: data.postalCode,
    stateOrProvince: data.stateOrProvince,
    countryIsocode: data.countryIsocode,
    businessContactName: data.countryIsocode,
    emailAddress: data.emailAddress,
    countryCallingCode: countryCallingCode,
    businessContactNumber: nationalNumber,
    countryofIncorporationIsocode: data.countryofIncorporationIsocode,
    noDUNSListed: true,
  };
};

export const getMappedCreateOwnerBusinessFields = ({
  data,
  person,
  business,
}) => {
  const fields = getMappedOwnerBusinessFields({ data, business });

  return {
    createdBy: person.emailAddress,
    ...fields,
  };
};

export const getMappedUpdateOwnerBusinessFields = ({
  data,
  person,
  business,
}) => {
  const fields = getMappedOwnerBusinessFields({ data, business });

  return {
    ...fields,
    addressLine2: data.addressLine2 || ' ',
    addressLine3: data.addressLine3 || ' ',
    emailAddress: data.emailAddress || ' ',
    modifiedBy: person.emailAddress,
    businessesOwnersBusinessesId: data.businessesOwnersBusinessesId,
  };
};

export const getMappedCreateBlobReferenceFields = ({
  file,
  person,
  business,
  blobReferencesTypesId,
}) => {
  const fileName = file.name.split('.');

  const formData = new FormData();

  formData.append('isActive', 'true');
  formData.append('createdBy', person.emailAddress);
  formData.append('businessesId', business.businessesId.toString());
  formData.append('blobReferencesTypesId', blobReferencesTypesId.toString());
  formData.append('blobFileSize', file.size.toString());
  formData.append('blobFileName', file.name);
  formData.append('blobFileExtension', fileName[1]);
  formData.append('file', file);

  return formData;
};

export const calcOwnershipPercentage = (
  { owners, hasFile },
  singleOwner25Percent,
) => {
  const owner = owners.find(
    (item) => item.isActive && item.ownershipPercentage >= 20,
  );

  const fields =
    singleOwner25Percent === false ? {} : getFormFields(owner || {});

  return calcProcentege({ ...fields, hasFile });
};

export const getMappedOwnerBlobReferences = (blobs) =>
  blobs.map((blob) => ({
    ...blob,
    id: blob.businessesOwnersBlobReferencesId,
  }));
